class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector("form");
    facetForm.addEventListener("input", this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector("#FacetsWrapperDesktop");
    if (facetWrapper) facetWrapper.addEventListener("keyup", onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener("popstate", onHistoryChange);

    // Initialize filter persistence on page load
    FacetFiltersForm.initFilterPersistence();
  }

  static initFilterPersistence() {
    // Store filter state when navigating away from collection pages
    window.addEventListener("beforeunload", FacetFiltersForm.saveFilterState);

    // Restore filter state when returning to collection pages
    if (FacetFiltersForm.isCollectionPage() && !window.location.search) {
      FacetFiltersForm.restoreFilterState();
    }
  }

  static isCollectionPage() {
    return (
      window.location.pathname.includes("/collections/") &&
      !window.location.pathname.includes("/products/")
    );
  }

  static getCollectionStorageKey() {
    const collectionPath = window.location.pathname
      .split("/collections/")[1]
      ?.split("/")[0];
    return collectionPath
      ? `shopify_collection_filters_${collectionPath}`
      : null;
  }

  static saveFilterState() {
    if (
      !FacetFiltersForm.isCollectionPage() ||
      !FacetFiltersForm.hasSessionStorage()
    )
      return;

    const storageKey = FacetFiltersForm.getCollectionStorageKey();
    if (!storageKey) return;

    try {
      const currentParams = window.location.search.slice(1);
      if (currentParams) {
        const filterState = {
          searchParams: currentParams,
          timestamp: Date.now(),
          pathname: window.location.pathname,
        };
        sessionStorage.setItem(storageKey, JSON.stringify(filterState));
      } else {
        // Clear storage if no filters are active
        sessionStorage.removeItem(storageKey);
      }
    } catch (e) {}
  }

  static restoreFilterState() {
    if (!FacetFiltersForm.hasSessionStorage()) return;

    const storageKey = FacetFiltersForm.getCollectionStorageKey();
    if (!storageKey) return;

    try {
      const savedState = sessionStorage.getItem(storageKey);
      if (!savedState) return;

      const filterState = JSON.parse(savedState);

      // Check if the saved state is recent (within 30 minutes)
      const thirtyMinutes = 30 * 60 * 1000;
      if (Date.now() - filterState.timestamp > thirtyMinutes) {
        sessionStorage.removeItem(storageKey);
        return;
      }

      // Verify we're on the same collection page
      if (filterState.pathname !== window.location.pathname) {
        return;
      }

      // Restore the filter state
      if (filterState.searchParams) {
        const newUrl = `${window.location.pathname}?${filterState.searchParams}`;
        history.replaceState(
          { searchParams: filterState.searchParams },
          "",
          newUrl,
        );

        // Update the initial search params to match restored state
        FacetFiltersForm.searchParamsInitial = filterState.searchParams;
        FacetFiltersForm.searchParamsPrev = filterState.searchParams;

        // Render the page with restored filters
        FacetFiltersForm.renderPage(filterState.searchParams, null, false);
      }
    } catch (e) {}
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll(".js-facet-remove").forEach((element) => {
      element.classList.toggle("disabled", disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById("ProductCount");
    const countContainerDesktop = document.getElementById(
      "ProductCountDesktop",
    );
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove("hidden"));
    document
      .getElementById("ProductGridContainer")
      .querySelector(".collection")
      .classList.add("loading");
    if (countContainer) {
      countContainer.classList.add("loading");
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add("loading");
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url },
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === "function")
          initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === "function")
      initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById("ProductGridContainer").innerHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductGridContainer").innerHTML;

    document
      .getElementById("ProductGridContainer")
      .querySelectorAll(".scroll-trigger")
      .forEach((element) => {
        element.classList.add("scroll-trigger--cancel");
      });
  }

  static renderProductCount(html) {
    const count = new DOMParser()
      .parseFromString(html, "text/html")
      .getElementById("ProductCount").innerHTML;
    const container = document.getElementById("ProductCount");
    const containerDesktop = document.getElementById("ProductCountDesktop");
    container.innerHTML = count;
    container.classList.remove("loading");
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove("loading");
    }
    const loadingSpinners = document.querySelectorAll(
      ".facets-container .loading__spinner, facet-filters-form .loading__spinner",
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add("hidden"));
  }

  static renderFilters(html, event) {
    // Enhanced safety checks for multiple scenarios
    const facetForm = document.getElementById("FacetFiltersForm");
    const facetFormMobile = document.getElementById("FacetFiltersFormMobile");
    const mainCollectionFilters = document.getElementById(
      "main-collection-filters",
    );
    const modernCarFilter = document.querySelector(".modern-car-filter");

    // Multiple checks to detect if native filters should be skipped
    const isNativeFiltersHidden =
      // Check main-collection-filters element
      (mainCollectionFilters &&
        (getComputedStyle(mainCollectionFilters).display === "none" ||
          getComputedStyle(mainCollectionFilters).visibility === "hidden" ||
          mainCollectionFilters.style.display === "none" ||
          mainCollectionFilters.style.visibility === "hidden")) ||
      // Check if custom filter system is present
      modernCarFilter !== null ||
      // Check if facet forms are missing or hidden
      !facetForm ||
      getComputedStyle(facetForm).display === "none" ||
      // Check for explicit hiding attributes
      (facetForm && facetForm.hasAttribute("hidden")) ||
      // Check parent container visibility
      (facetForm &&
        facetForm.closest(".facets-container, facet-filters-form") &&
        getComputedStyle(
          facetForm.closest(".facets-container, facet-filters-form"),
        ).display === "none");

    if (isNativeFiltersHidden) {
      return;
    }

    if (!facetForm && !facetFormMobile) {
      return;
    }

    try {
      const parsedHTML = new DOMParser().parseFromString(html, "text/html");
      const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
        "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
      );
      const facetDetailsElementsFromDom = document.querySelectorAll(
        "#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter",
      );

      // Remove facets that are no longer returned from the server
      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        try {
          if (
            !Array.from(facetDetailsElementsFromFetch).some(
              ({ id }) => currentElement.id === id,
            )
          ) {
            currentElement.remove();
          }
        } catch (e) {}
      });

      const matchesId = (element) => {
        try {
          const jsFilter = event
            ? event.target.closest(".js-filter")
            : undefined;
          return jsFilter ? element.id === jsFilter.id : false;
        } catch (e) {
          return false;
        }
      };

      const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter(
        (element) => !matchesId(element),
      );
      const countsToRender = Array.from(facetDetailsElementsFromFetch).find(
        matchesId,
      );

      facetsToRender.forEach((elementToRender, index) => {
        try {
          const currentElement = document.getElementById(elementToRender.id);
          // Element already rendered in the DOM so just update the innerHTML
          if (currentElement) {
            document.getElementById(elementToRender.id).innerHTML =
              elementToRender.innerHTML;
          } else {
            if (index > 0) {
              const {
                className: previousElementClassName,
                id: previousElementId,
              } = facetsToRender[index - 1];
              // Same facet type (eg horizontal/vertical or drawer/mobile)
              if (elementToRender.className === previousElementClassName) {
                const prevElement = document.getElementById(previousElementId);
                if (prevElement) {
                  prevElement.after(elementToRender);
                  return;
                }
              }
            }

            if (elementToRender.parentElement) {
              const parentId = elementToRender.parentElement.id;
              let targetElement = null;
              if (parentId) {
                targetElement = document.querySelector(
                  `#${parentId} .js-filter`,
                );
              }
              if (!targetElement && elementToRender.parentElement.classList?.contains("facets__wrapper--row-filters")) {
                const rowWrapper = document.querySelector(
                  "#FacetsWrapperDesktop .facets__wrapper--row-filters",
                );
                if (rowWrapper) {
                  const insertAfter =
                    index === 0
                      ? rowWrapper.querySelector(".js-search-part-type")
                      : document.getElementById(facetsToRender[index - 1].id);
                  if (insertAfter) {
                    insertAfter.after(elementToRender);
                  }
                }
              } else if (targetElement) {
                targetElement.before(elementToRender);
              }
            }
          }
        } catch (e) {}
      });

      FacetFiltersForm.renderActiveFacets(parsedHTML);
      FacetFiltersForm.renderAdditionalElements(parsedHTML);

      if (countsToRender && event && event.target) {
        try {
          const closestJSFilter = event.target.closest(".js-filter");
          if (closestJSFilter) {
            const closestJSFilterID = closestJSFilter.id;

            if (closestJSFilterID) {
              FacetFiltersForm.renderCounts(countsToRender, closestJSFilter);
              FacetFiltersForm.renderMobileCounts(
                countsToRender,
                document.getElementById(closestJSFilterID),
              );

              const newFacetDetailsElement =
                document.getElementById(closestJSFilterID);
              if (newFacetDetailsElement) {
                const newElementSelector =
                  newFacetDetailsElement.classList.contains(
                    "mobile-facets__details",
                  )
                    ? `.mobile-facets__close-button`
                    : `.facets__summary`;
                const newElementToActivate =
                  newFacetDetailsElement.querySelector(newElementSelector);

                const isTextInput =
                  event.target.getAttribute("type") === "text";

                if (newElementToActivate && !isTextInput)
                  newElementToActivate.focus();
              }
            }
          }
        } catch (e) {}
      }
    } catch (e) {
      console.error("Error in renderFilters:", e);
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = [
      ".active-facets-mobile",
      ".active-facets-desktop",
    ];

    activeFacetElementSelectors.forEach((selector) => {
      try {
        const activeFacetsElement = html.querySelector(selector);
        if (!activeFacetsElement) return;

        const targetElement = document.querySelector(selector);
        if (targetElement) {
          targetElement.innerHTML = activeFacetsElement.innerHTML;
        }
      } catch (e) {}
    });

    try {
      FacetFiltersForm.toggleActiveFacets(false);
    } catch (e) {}
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = [
      ".mobile-facets__open",
      ".mobile-facets__count",
      ".sorting",
    ];

    mobileElementSelectors.forEach((selector) => {
      try {
        const sourceElement = html.querySelector(selector);
        if (!sourceElement) return;

        const targetElement = document.querySelector(selector);
        if (targetElement) {
          targetElement.innerHTML = sourceElement.innerHTML;
        }
      } catch (e) {}
    });

    try {
      const mobileForm = document.getElementById("FacetFiltersFormMobile");
      if (mobileForm) {
        const menuDrawer = mobileForm.closest("menu-drawer");
        if (menuDrawer && typeof menuDrawer.bindEvents === "function") {
          menuDrawer.bindEvents();
        }
      }
    } catch (e) {}
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector(".facets__summary");
    const sourceSummary = source.querySelector(".facets__summary");

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector(".facets__header");
    const sourceHeaderElement = source.querySelector(".facets__header");

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector(".facets-wrap");
    const sourceWrapElement = source.querySelector(".facets-wrap");

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(
        target.querySelector("show-more-button .label-show-more.hidden"),
      );
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll(".facets__item.hidden")
          .forEach((hiddenItem) =>
            hiddenItem.classList.replace("hidden", "show-more-item"),
          );
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector(".mobile-facets__list");
    const sourceFacetsList = source.querySelector(".mobile-facets__list");

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );

    // Save filter state whenever URL is updated
    FacetFiltersForm.saveFilterState();
  }

  static hasSessionStorage() {
    try {
      const test = "__sessionStorageTest__";
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      return false;
    }
  }

  static getSections() {
    return [
      {
        section: document.getElementById("product-grid").dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    if (event.srcElement.className !== "facets__search-input") {
      const sortFilterForms = document.querySelectorAll(
        "facet-filters-form form",
      );
      if (event.srcElement.className == "mobile-facets__checkbox") {
        const searchParams = this.createSearchParams(
          event.target.closest("form"),
        );
        this.onSubmitForm(searchParams, event);
      } else {
        const forms = [];
        const isMobile =
          event.target.closest("form").id === "FacetFiltersFormMobile";

        sortFilterForms.forEach((form) => {
          if (!isMobile) {
            if (
              form.id === "FacetSortForm" ||
              form.id === "FacetFiltersForm" ||
              form.id === "FacetSortDrawerForm"
            ) {
              forms.push(this.createSearchParams(form));
            }
          } else if (form.id === "FacetFiltersFormMobile") {
            forms.push(this.createSearchParams(form));
          }
        });
        this.onSubmitForm(forms.join("&"), event);
      }
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf("?") == -1
        ? ""
        : event.currentTarget.href.slice(
            event.currentTarget.href.indexOf("?") + 1,
          );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll("input.field__price").forEach((element) => {
      element.addEventListener("change", this.onRangeChange.bind(this));
      element.addEventListener("keydown", this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern =
      /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll("input");
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute("data-max", maxInput.value);
    if (minInput.value) maxInput.setAttribute("data-min", minInput.value);
    if (minInput.value === "") maxInput.setAttribute("data-min", 0);
    if (maxInput.value === "")
      minInput.setAttribute("data-max", maxInput.getAttribute("data-max"));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute("data-min"));
    const max = Number(input.getAttribute("data-max"));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define("price-range", PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector("a");
    facetLink.setAttribute("role", "button");
    facetLink.addEventListener("click", this.closeFilter.bind(this));
    facetLink.addEventListener("keyup", (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === "SPACE") this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form =
      this.closest("facet-filters-form") ||
      document.querySelector("facet-filters-form");
    form.onActiveFilterClick(event);
  }
}

customElements.define("facet-remove", FacetRemove);
